<!-- AbsenceModal.vue -->
<template>
    <CustomDialog v-model="localVisible">
        <!-- HEADER -->
        <template #title>
            <div class="ppv2-flex ppv2-items-center ppv2-justify-between ppv2-w-full">
                <span class="ppv2-text-lg poppinsSemiBold">
                    {{
                        isEditingAbsence
                            ? $t('staff_planning_v3.components.absence_modal.title_edit')
                            : $t('staff_planning_v3.components.absence_modal.title_add')
                    }}
                    <span v-if="selectedAbsenceStaff">
                        {{ selectedAbsenceStaff?.user?.first_name || selectedAbsenceStaff?.resource_externe?.uid }}
                        {{
                            selectedAbsenceStaff?.user?.last_name.charAt(0) ||
                            selectedAbsenceStaff?.resource_externe?.uid.charAt(0)
                        }}.
                    </span>
                </span>
            </div>
        </template>

        <!-- CONTENT -->
        <template #content>
            <div class="ppv2-space-y-6">
                <!-- Employee Select -->
                <div v-if="!isEditingAbsence">
                    <BaseSelect
                        v-model="localForm.staff_id"
                        :options="staffResouerceList"
                        :label="$t('staff_planning_v3.components.absence_modal.employee')"
                        :placeholder="$t('staff_planning_v3.components.absence_modal.select_employee')"
                        :custom-size-label="10"
                        custom-label-class="poppinsSemiBold text-uppercase !ppv2-text-[#A09DBC]"
                        fixed-dropdown
                    />
                </div>

                <!-- Motif -->
                <div>
                    <BaseSelect
                        v-model="localForm.motif"
                        :options="motifOptions"
                        :label="$t('staff_planning_v3.components.absence_modal.motif')"
                        :placeholder="$t('staff_planning_v3.components.absence_modal.select_motif')"
                        :custom-size-label="10"
                        custom-label-class="poppinsSemiBold text-uppercase !ppv2-text-[#A09DBC]"
                        fixed-dropdown
                    />
                </div>

                <!-- Planning Section -->
                <div class="ppv2-space-y-4">
                    <div class="v3-section-title mb-2">
                        <span>{{ $t('staff_planning_v3.components.absence_modal.planning') }}</span>
                    </div>

                    <PlanningPeriodSection
                        :date="localForm.date"
                        @update:date="localForm.date = $event"
                        :periods="localForm.periods"
                        @update:periods="localForm.periods = $event"
                        :labels="{
                            date: $t('staff_planning_v3.components.absence_modal.date'),
                            startTime: $t('staff_planning_v3.components.absence_modal.start_time'),
                            endTime: $t('staff_planning_v3.components.absence_modal.end_time'),
                            addSchedule: $t('staff_planning_v3.components.absence_modal.add_schedule'),
                        }"
                    />
                </div>

                <!-- Advanced Options -->
                <div class="ppv2-space-y-3">
                    <div class="v3-section-title mb-2">
                        <span>{{ $t('staff_planning_v3.components.absence_modal.advanced_options') }}</span>
                    </div>

                    <div class="ppv2-space-y-3">
                        <!-- Recurrence -->
                        <RecurrenceAccordion
                            :frequency="localForm.frequency"
                            @update:frequency="localForm.frequency = $event"
                            :recurrenceEndDate="localForm.recurrence_end_date"
                            @update:recurrenceEndDate="localForm.recurrence_end_date = $event"
                            :selectedDays="selectedDays"
                            :days="days"
                            :frequencyOptions="frequencyOptions"
                            :chooseDaysText="$t('staff_planning_v3.components.absence_modal.choose_days')"
                            @toggle-day="toggleDay"
                        />
                        <div>
                            <p class="ppv2-text-xs ppv2-text-gray-500">
                                {{ $t('staff_planning_v3.components.absence_modal.copy_desc') }}
                            </p>
                        </div>
                    </div>
                </div>

                <!-- Note -->
                <NoteSection v-model="localForm.comment" />
            </div>
        </template>

        <!-- ACTIONS -->
        <template #actions>
            <ModalActions
                @cancel="closeModal"
                @confirm="submit"
                :confirmLabel="
                    isEditingAbsence
                        ? $t('staff_planning_v3.components.absence_modal.edit_absence')
                        : $t('staff_planning_v3.components.absence_modal.add_absence')
                "
                :disabled="hasSupEqualStartEnd"
            />
        </template>
    </CustomDialog>
</template>

<script>
import { ArrowRight, Plus } from 'lucide-vue'
import CustomDialog from '../../../Shared/CustomDialog.vue'
import BaseSelect from '../../../Shared/BaseSelect.vue'
import BaseDateInput from '../../../Shared/BaseDateInput.vue'
import BaseButton from '../../../Shared/BaseButton.vue'
import BaseTimeInput from '../../../Shared/BaseTimeInput.vue'
import NoteSection from './NoteSection.vue'
import RecurrenceAccordion from './RecurrenceAccordion.vue'
import CollapsibleAccordion from './CollapsibleAccordion.vue'
import PlanningPeriodSection from './PlanningPeriodSection.vue'
import ModalActions from './ModalActions.vue'
export default {
    name: 'AbsenceModal',

    components: {
        ArrowRight,
        Plus,
        CustomDialog,
        BaseSelect,
        BaseDateInput,
        BaseButton,
        BaseTimeInput,
        NoteSection,
        RecurrenceAccordion,
        CollapsibleAccordion,
        PlanningPeriodSection,
        ModalActions,
    },

    props: {
        value: {
            type: Boolean,
            default: false,
        },

        absenceForm: {
            type: Object,
            required: true,
        },

        selectedAbsenceStaff: {
            type: Object,
            default: null,
        },

        isEditingAbsence: {
            type: Boolean,
            default: false,
        },

        staffResouerceList: {
            type: Array,
            default: () => [],
        },
    },

    emits: ['input', 'save'],

    data() {
        return {
            localForm: {
                ...this.absenceForm,
                periods: this.absenceForm.periods
                    ? this.absenceForm.periods.map((p) => ({ ...p }))
                    : [{ start: '08:00', end: '18:00' }],
            },

            selectedDays: [],

            days: [
                { id: 1, label: 'L' },
                { id: 2, label: 'M' },
                { id: 3, label: 'M' },
                { id: 4, label: 'J' },
                { id: 5, label: 'V' },
                { id: 6, label: 'S' },
                { id: 7, label: 'D' },
            ],

            motifOptions: [
                { label: this.$t('staff_planning_v3.components.absence_modal.paid_leave'), value: 'Congé payé' },
                { label: this.$t('staff_planning_v3.components.absence_modal.sickness'), value: 'Maladie' },
                { label: this.$t('staff_planning_v3.components.absence_modal.rest'), value: 'Repos' },
                { label: this.$t('staff_planning_v3.components.absence_modal.other'), value: 'Autre' },
            ],

            frequencyOptions: [
                { label: this.$t('staff_planning_v3.components.absence_modal.every_week'), value: 'Toutes les semaines' },
                { label: this.$t('staff_planning_v3.components.absence_modal.every_2_weeks'), value: 'Toutes les 2 semaines' },
                { label: this.$t('staff_planning_v3.components.absence_modal.every_month'), value: 'Tous les mois' },
            ],
        }
    },

    computed: {
        localVisible: {
            get() {
                return this.value
            },
            set(val) {
                this.$emit('input', val)
            },
        },
        hasSupEqualStartEnd() {
            return this.localForm.periods.some((period) => period.start >= period.end)
        },
    },

    watch: {
        absenceForm: {
            deep: true,
            handler(newVal) {
                this.localForm = {
                    ...newVal,
                    periods: newVal.periods ? newVal.periods.map((p) => ({ ...p })) : [{ start: '08:00', end: '18:00' }],
                }
            },
        },
    },

    methods: {
        closeModal() {
            this.$emit('input', false)
        },

        submit() {
            this.$emit('save', {
                ...this.localForm,
                selectedDays: this.selectedDays,
            })
        },

        toggleDay(dayId) {
            if (this.selectedDays.includes(dayId)) {
                this.selectedDays = this.selectedDays.filter((d) => d !== dayId)
            } else {
                this.selectedDays.push(dayId)
            }
        },
    },
}
</script>
